import React, { useEffect, useState, useCallback } from 'react';

const API_BASE = '/api/v1/tally-push';

const MODULE_OPTIONS = ['PMS', 'KOT', 'BAR', 'BANQUET', 'STORES', 'STORES_CONSUMPTION'];

const LEDGER_SECTIONS = {
  Revenue: ['ROOM_TARIFF', 'LAUNDRY', 'ROOM_SERVICE', 'MINIBAR', 'LATE_CHECKOUT', 'MISC_CHARGE'],
  'F&B': ['KOT_SALES', 'BAR_SALES', 'BANQUET_REVENUE'],
  Payment: ['CASH', 'CARD', 'UPI', 'CHEQUE', 'WALLET', 'CORPORATE', 'TRAVEL_AGENT', 'CITY_LEDGER'],
  Tax: ['CGST', 'SGST', 'IGST'],
  Other: ['GUEST_DEBTOR', 'ADVANCE'],
  'Stores - purchases & payments': ['PURCHASE_FNB', 'PURCHASE_HK', 'PURCHASE_MNT', 'PURCHASE_OTHER', 'INPUT_CGST', 'INPUT_SGST', 'INPUT_IGST', 'FREIGHT_INWARD', 'ROUND_OFF', 'BANK'],
  'Stores - consumption journals': ['CONSUMPTION_FNB', 'CONSUMPTION_HK', 'CONSUMPTION_MNT', 'CONSUMPTION_OTHER', 'INVENTORY_FNB', 'INVENTORY_HK', 'INVENTORY_MNT', 'INVENTORY_OTHER', 'WASTAGE', 'STOCK_ADJUSTMENT']
};

const emptyForm = {
  profile_gstin: '',
  profile_name: '',
  tally_host_url: '',
  tally_company_name: '',
  modules: [],
  auto_push_night_audit: false,
  ledger_mappings: {}
};

const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.42)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 };
const modalStyle = { width: 'min(720px,96vw)', maxHeight: '92vh', overflowY: 'auto', background: '#fff', borderRadius: 10, border: '1px solid #d9e2ec', padding: '1.4rem 1.6rem', position: 'relative' };
const closeBtnStyle = { position: 'absolute', top: '0.5rem', right: '0.6rem', border: '1px solid #cbd5e0', background: '#fff', borderRadius: 4, cursor: 'pointer', fontSize: 16, padding: '2px 8px' };

const btnPrimary = { background: '#1a365d', color: '#fff', border: 'none', borderRadius: 6, padding: '0.45rem 1rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.88rem' };
const btnWarning = { background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 6, padding: '0.35rem 0.8rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem' };
const btnDanger = { background: '#ef4444', color: '#fff', border: 'none', borderRadius: 6, padding: '0.35rem 0.8rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem' };
const btnSecondary = { background: '#e2e8f0', color: '#1a365d', border: '1px solid #cbd5e0', borderRadius: 6, padding: '0.35rem 0.8rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem' };

const labelStyle = { display: 'grid', gap: '0.25rem' };
const labelTextStyle = { fontSize: '0.82rem', fontWeight: 600, color: '#334155' };
const inputStyle = { padding: '0.4rem 0.55rem', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: '0.9rem', width: '100%', boxSizing: 'border-box' };
const badgeStyle = (color) => ({ display: 'inline-block', background: color || '#e2e8f0', color: '#fff', borderRadius: 4, padding: '2px 8px', fontSize: '0.75rem', fontWeight: 700, marginRight: 4 });

const MODULE_COLORS = { PMS: '#1a365d', KOT: '#0d9488', BAR: '#7c3aed', BANQUET: '#c2410c', STORES: '#0f766e', STORES_CONSUMPTION: '#475569' };

const humanize = (key) => key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

function TallyPushSettings({ token, selectedProperty }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [defaultMappings, setDefaultMappings] = useState({});
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState('');

  // Test connection state
  const [testingId, setTestingId] = useState(null);
  const [testResult, setTestResult] = useState({}); // { [id]: { ok, msg } }

  // Billing profiles (dropdown source)
  const [billingProfiles, setBillingProfiles] = useState([]);

  // Collapsible ledger sections
  const [expandedSections, setExpandedSections] = useState({});

  const headers = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || ''
  }), [token, selectedProperty]);

  const loadConfigs = useCallback(async () => {
    if (!selectedProperty?.id) { setRows([]); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/configs`, { headers: headers() });
      const data = await res.json().catch(() => []);
      if (!res.ok) throw new Error(data?.error || 'Failed to load Tally configurations');
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || 'Failed to load Tally configurations');
    } finally {
      setLoading(false);
    }
  }, [selectedProperty, headers]);

  const loadBillingProfiles = useCallback(async () => {
    if (!selectedProperty?.id) return;
    try {
      const res = await fetch(`${API_BASE}/billing-profiles`, { headers: headers() });
      const data = await res.json().catch(() => []);
      setBillingProfiles(Array.isArray(data) ? data : []);
    } catch (_) { setBillingProfiles([]); }
  }, [selectedProperty, headers]);

  const loadDefaultMappings = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/default-mappings`, { headers: headers() });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data && typeof data === 'object') {
        setDefaultMappings(data);
      }
    } catch (_) {
      // silent — defaults are optional
    }
  }, [headers]);

  useEffect(() => {
    setError('');
    setMessage('');
    loadConfigs();
    loadDefaultMappings();
    loadBillingProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedProperty?.id]);

  const openAddModal = () => {
    setEditingId(null);
    setModalError('');
    // Pre-fill ledger mappings with defaults
    setForm({ ...emptyForm, ledger_mappings: { ...defaultMappings } });
    setExpandedSections({});
    setShowModal(true);
  };

  const openEditModal = (row) => {
    setEditingId(row.config_id);
    setModalError('');
    const existingMappings = row.ledger_mappings && typeof row.ledger_mappings === 'object' ? row.ledger_mappings : {};
    // Merge defaults underneath existing so any new keys appear
    const merged = { ...defaultMappings, ...existingMappings };
    setForm({
      profile_gstin: row.profile_gstin || '',
      profile_name: row.profile_name || '',
      tally_host_url: row.tally_host_url || '',
      tally_company_name: row.tally_company_name || '',
      modules: Array.isArray(row.modules) ? [...row.modules] : [],
      auto_push_night_audit: row.auto_push_night_audit === true,
      ledger_mappings: merged
    });
    setExpandedSections({});
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setForm({ ...emptyForm });
    setModalError('');
  };

  const toggleModule = (mod) => {
    setForm((prev) => {
      const cur = prev.modules || [];
      return { ...prev, modules: cur.includes(mod) ? cur.filter((m) => m !== mod) : [...cur, mod] };
    });
  };

  const setMapping = (key, value) => {
    setForm((prev) => ({ ...prev, ledger_mappings: { ...prev.ledger_mappings, [key]: value } }));
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const validateForm = () => {
    if (!String(form.profile_gstin || '').trim()) return 'Select a Billing Profile';
    if (!String(form.tally_host_url || '').trim()) return 'Tally Host URL is required';
    try { new URL(form.tally_host_url.trim()); } catch { return 'Tally Host URL must be a valid URL'; }
    if (!String(form.tally_company_name || '').trim()) return 'Tally Company Name is required';
    if (!form.modules || form.modules.length === 0) return 'Select at least one module';
    return '';
  };

  const saveConfig = async () => {
    setModalError('');
    const err = validateForm();
    if (err) { setModalError(err); return; }
    setSaving(true);
    try {
      const payload = {
        profile_gstin: form.profile_gstin.trim().toUpperCase(),
        profile_name: form.profile_name.trim(),
        tally_host_url: form.tally_host_url.trim(),
        tally_company_name: form.tally_company_name.trim(),
        modules: form.modules,
        auto_push_night_audit: form.auto_push_night_audit === true,
        ledger_mappings: form.ledger_mappings || {}
      };
      const url = editingId ? `${API_BASE}/configs/${editingId}` : `${API_BASE}/configs`;
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: headers(), body: JSON.stringify(payload) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to save configuration');
      setMessage(editingId ? 'Configuration updated successfully.' : 'Configuration created successfully.');
      closeModal();
      loadConfigs();
    } catch (err2) {
      setModalError(err2?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const deleteConfig = async (id) => {
    if (!window.confirm('Are you sure you want to deactivate this Tally connection?')) return;
    setError('');
    try {
      const res = await fetch(`${API_BASE}/configs/${id}`, { method: 'DELETE', headers: headers() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Delete failed');
      setMessage('Connection deactivated.');
      loadConfigs();
    } catch (err2) {
      setError(err2?.message || 'Delete failed');
    }
  };

  const testConnection = async (id) => {
    setTestingId(id);
    setTestResult((prev) => ({ ...prev, [id]: null }));
    try {
      const res = await fetch(`${API_BASE}/configs/${id}/test`, { method: 'POST', headers: headers() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Connection test failed');
      setTestResult((prev) => ({ ...prev, [id]: { ok: true, msg: data?.message || 'Connection successful!' } }));
    } catch (err2) {
      setTestResult((prev) => ({ ...prev, [id]: { ok: false, msg: err2?.message || 'Connection test failed' } }));
    } finally {
      setTestingId(null);
    }
  };

  const toggleActive = async (row) => {
    // Quick toggle: use PUT to flip is_active (we re-send the config with toggled state)
    setError('');
    try {
      const payload = {
        profile_gstin: row.profile_gstin,
        profile_name: row.profile_name,
        tally_host_url: row.tally_host_url,
        tally_company_name: row.tally_company_name,
        modules: Array.isArray(row.modules) ? row.modules : [],
        auto_push_night_audit: row.auto_push_night_audit === true,
        ledger_mappings: row.ledger_mappings || {},
        is_active: !row.is_active
      };
      const res = await fetch(`${API_BASE}/configs/${row.config_id}`, { method: 'PUT', headers: headers(), body: JSON.stringify(payload) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Toggle failed');
      loadConfigs();
    } catch (err2) {
      setError(err2?.message || 'Toggle failed');
    }
  };

  return (
    <div style={{ padding: '1rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <h3 style={{ margin: 0, color: '#1a365d' }}>Tally Live Push Settings</h3>
        <button style={btnPrimary} onClick={openAddModal}>+ Add Connection</button>
      </div>

      {/* Messages */}
      {error ? <div style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fca5a5', borderRadius: 6, padding: '0.55rem 0.8rem', marginBottom: '0.8rem', fontSize: '0.88rem' }}>{error}</div> : null}
      {message ? <div style={{ background: '#f0fdf4', color: '#166534', border: '1px solid #86efac', borderRadius: 6, padding: '0.55rem 0.8rem', marginBottom: '0.8rem', fontSize: '0.88rem' }}>{message}</div> : null}

      {/* Loading */}
      {loading ? <div style={{ padding: '1rem', color: '#64748b', fontSize: '0.9rem' }}>Loading configurations...</div> : null}

      {/* Connection Table */}
      {!loading ? (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#1a365d', color: '#fff' }}>
                <th style={{ padding: '0.5rem 0.6rem', textAlign: 'left', fontSize: '0.85rem' }}>Profile Name</th>
                <th style={{ padding: '0.5rem 0.6rem', textAlign: 'left', fontSize: '0.85rem' }}>GSTIN</th>
                <th style={{ padding: '0.5rem 0.6rem', textAlign: 'left', fontSize: '0.85rem' }}>Tally Company</th>
                <th style={{ padding: '0.5rem 0.6rem', textAlign: 'left', fontSize: '0.85rem' }}>Host URL</th>
                <th style={{ padding: '0.5rem 0.6rem', textAlign: 'left', fontSize: '0.85rem' }}>Modules</th>
                <th style={{ padding: '0.5rem 0.6rem', textAlign: 'center', fontSize: '0.85rem' }}>Active</th>
                <th style={{ padding: '0.5rem 0.6rem', textAlign: 'left', fontSize: '0.85rem' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.config_id} style={{ borderBottom: '1px solid #e2e8f0', background: row.is_active !== false ? '#fff' : '#f8fafc' }}>
                  <td style={{ padding: '0.5rem 0.6rem', fontSize: '0.88rem', fontWeight: 500 }}>{row.profile_name || '-'}</td>
                  <td style={{ padding: '0.5rem 0.6rem', fontSize: '0.85rem', fontFamily: 'monospace' }}>{row.profile_gstin || '-'}</td>
                  <td style={{ padding: '0.5rem 0.6rem', fontSize: '0.88rem' }}>{row.tally_company_name || '-'}</td>
                  <td style={{ padding: '0.5rem 0.6rem', fontSize: '0.82rem', fontFamily: 'monospace', color: '#475569' }}>{row.tally_host_url || '-'}</td>
                  <td style={{ padding: '0.5rem 0.6rem' }}>
                    {(Array.isArray(row.modules) ? row.modules : []).map((m) => (
                      <span key={m} style={badgeStyle(MODULE_COLORS[m] || '#64748b')}>{m}</span>
                    ))}
                    {(!row.modules || row.modules.length === 0) ? <span style={{ color: '#94a3b8', fontSize: '0.82rem' }}>None</span> : null}
                  </td>
                  <td style={{ padding: '0.5rem 0.6rem', textAlign: 'center' }}>
                    <label style={{ cursor: 'pointer', position: 'relative', display: 'inline-block', width: 40, height: 22 }}>
                      <input
                        type="checkbox"
                        checked={row.is_active !== false}
                        onChange={() => toggleActive(row)}
                        style={{ opacity: 0, width: 0, height: 0 }}
                      />
                      <span style={{
                        position: 'absolute', inset: 0, borderRadius: 11,
                        background: row.is_active !== false ? '#22c55e' : '#cbd5e0',
                        transition: 'background 0.2s'
                      }} />
                      <span style={{
                        position: 'absolute', top: 2, left: row.is_active !== false ? 20 : 2,
                        width: 18, height: 18, borderRadius: '50%', background: '#fff',
                        transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.15)'
                      }} />
                    </label>
                  </td>
                  <td style={{ padding: '0.5rem 0.6rem' }}>
                    <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', alignItems: 'center' }}>
                      <button style={btnSecondary} onClick={() => openEditModal(row)}>Edit</button>
                      <button
                        style={{ ...btnWarning, opacity: testingId === row.config_id ? 0.6 : 1 }}
                        onClick={() => testConnection(row.config_id)}
                        disabled={testingId === row.config_id}
                      >
                        {testingId === row.config_id ? 'Testing...' : 'Test'}
                      </button>
                      <button style={btnDanger} onClick={() => deleteConfig(row.config_id)}>Delete</button>
                    </div>
                    {/* Inline test result */}
                    {testResult[row.config_id] ? (
                      <div style={{
                        marginTop: 4, fontSize: '0.8rem', fontWeight: 500,
                        color: testResult[row.config_id].ok ? '#166534' : '#b91c1c'
                      }}>
                        {testResult[row.config_id].msg}
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ padding: '1.2rem', color: '#94a3b8', textAlign: 'center', fontSize: '0.9rem' }}>
                    No Tally connections configured. Click "Add Connection" to get started.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      ) : null}

      {/* Add / Edit Modal */}
      {showModal ? (
        <div style={overlayStyle} onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <div style={modalStyle}>
            <button style={closeBtnStyle} onClick={closeModal} title="Close">&times;</button>
            <h4 style={{ margin: '0 0 1rem', color: '#1a365d', fontSize: '1.1rem' }}>
              {editingId ? 'Edit Tally Connection' : 'Add Tally Connection'}
            </h4>

            {modalError ? (
              <div style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fca5a5', borderRadius: 6, padding: '0.5rem 0.75rem', marginBottom: '0.8rem', fontSize: '0.85rem' }}>
                {modalError}
              </div>
            ) : null}

            {/* Form fields */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.8rem', marginBottom: '1rem' }}>
              <label style={labelStyle}>
                <span style={labelTextStyle}>Billing Profile *</span>
                <select
                  style={inputStyle}
                  value={form.profile_gstin}
                  disabled={!!editingId}
                  onChange={(e) => {
                    const val = e.target.value;
                    const match = billingProfiles.find((p) => String(p.billing_profile_id) === val);
                    if (match) {
                      setForm((prev) => ({
                        ...prev,
                        profile_gstin: match.gstin_number || String(match.billing_profile_id),
                        profile_name: match.profile_name || prev.profile_name,
                        tally_company_name: prev.tally_company_name || match.billing_company_name || ''
                      }));
                    }
                  }}
                >
                  <option value="">— Select Billing Profile —</option>
                  {billingProfiles.map((bp) => (
                    <option key={bp.billing_profile_id} value={bp.billing_profile_id}>
                      {bp.profile_name}{bp.gstin_number ? ` — ${bp.gstin_number}` : ''}{bp.billing_company_name ? ` (${bp.billing_company_name})` : ''} [{bp.payer_type}]
                    </option>
                  ))}
                </select>
                {!billingProfiles.length && (
                  <div style={{ fontSize: '0.75rem', color: '#b91c1c', marginTop: 2 }}>
                    No billing profiles found. Set up in Front Office → Billing Info first.
                  </div>
                )}
              </label>
              <label style={labelStyle}>
                <span style={labelTextStyle}>Profile Name</span>
                <input
                  style={inputStyle}
                  value={form.profile_name}
                  placeholder="Grand Hotel - Rooms"
                  onChange={(e) => setForm({ ...form, profile_name: e.target.value })}
                />
              </label>
              <label style={labelStyle}>
                <span style={labelTextStyle}>Tally Host URL *</span>
                <input
                  style={inputStyle}
                  value={form.tally_host_url}
                  placeholder="http://192.168.1.50:9000"
                  onChange={(e) => setForm({ ...form, tally_host_url: e.target.value })}
                />
              </label>
              <label style={labelStyle}>
                <span style={labelTextStyle}>Tally Company Name *</span>
                <input
                  style={inputStyle}
                  value={form.tally_company_name}
                  placeholder="Grand Hotel Pvt Ltd"
                  onChange={(e) => setForm({ ...form, tally_company_name: e.target.value })}
                />
              </label>
            </div>

            {/* Modules */}
            <div style={{ marginBottom: '1rem' }}>
              <span style={{ ...labelTextStyle, display: 'block', marginBottom: '0.4rem' }}>Modules *</span>
              <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap' }}>
                {MODULE_OPTIONS.map((mod) => (
                  <label key={mod} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.9rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={(form.modules || []).includes(mod)}
                      onChange={() => toggleModule(mod)}
                    />
                    <span style={{ fontWeight: 500 }}>{mod}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Auto-push toggle */}
            <div style={{ marginBottom: '1.2rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', fontSize: '0.9rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={form.auto_push_night_audit === true}
                  onChange={(e) => setForm({ ...form, auto_push_night_audit: e.target.checked })}
                />
                <span style={{ fontWeight: 500 }}>Auto-push on Night Audit</span>
              </label>
              <div style={{ fontSize: '0.78rem', color: '#64748b', marginLeft: '1.6rem', marginTop: 2 }}>
                When enabled, vouchers will be pushed to Tally automatically during Night Audit.
              </div>
            </div>

            {/* Ledger Mappings */}
            <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '0.8rem' }}>
              <span style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1a365d', display: 'block', marginBottom: '0.6rem' }}>
                Ledger Mappings
              </span>
              <div style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: '0.6rem' }}>
                Map each posting category to the corresponding Tally ledger name. Defaults are pre-filled.
              </div>

              {Object.entries(LEDGER_SECTIONS).map(([section, keys]) => {
                const isExpanded = expandedSections[section] === true;
                return (
                  <div key={section} style={{ marginBottom: '0.5rem', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                    {/* Section header — click to toggle */}
                    <div
                      onClick={() => toggleSection(section)}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '0.55rem 0.75rem', background: '#f8fafc', cursor: 'pointer', userSelect: 'none'
                      }}
                    >
                      <span style={{ fontWeight: 600, fontSize: '0.88rem', color: '#334155' }}>{section}</span>
                      <span style={{ fontSize: '0.8rem', color: '#94a3b8', fontWeight: 700 }}>{isExpanded ? '\u25B2' : '\u25BC'}</span>
                    </div>
                    {/* Section body */}
                    {isExpanded ? (
                      <div style={{ padding: '0.6rem 0.75rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '0.55rem' }}>
                        {keys.map((key) => (
                          <label key={key} style={{ display: 'grid', gap: '0.2rem' }}>
                            <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569' }}>{humanize(key)}</span>
                            <input
                              style={{ ...inputStyle, fontSize: '0.85rem', padding: '0.35rem 0.5rem' }}
                              value={form.ledger_mappings[key] || ''}
                              placeholder={`Tally ledger for ${humanize(key)}`}
                              onChange={(e) => setMapping(key, e.target.value)}
                            />
                          </label>
                        ))}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>

            {/* Modal actions */}
            <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end', marginTop: '1.2rem', paddingTop: '0.8rem', borderTop: '1px solid #e2e8f0' }}>
              <button style={btnSecondary} onClick={closeModal}>Cancel</button>
              <button
                style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}
                onClick={saveConfig}
                disabled={saving}
              >
                {saving ? 'Saving...' : (editingId ? 'Update Connection' : 'Save Connection')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default TallyPushSettings;
